/**
 * "Your Cadence" — the activity panel on the logged-in home page.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  JUL      SEP  │ YOUR CADENCE 🔥4  │ THE TEAM                │
 *   │ Mon ▪ ▫ ▪ ░ ░  │ 18 of 28 days     │ 4 of 6 out there        │
 *   │     ▪ ▪ ▫ ░ ░  │                   │                         │
 *   │ Wed ▫ ▪ ▪ ░ ░  │ Strong stretch —  │ 127 visits  43 accounts │
 *   │     ▪ ▫ ▪ ░ ░  │ out there most    │                         │
 *   │ Fri ▪ ▪ ▫ ░ ░  │ days.             │                         │
 *   │     · · · ░ ░  │ 47 visits 23 acct │                         │
 *   │     · · · ░ ░  │ Jul 30 – Aug 26   │                         │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * "Cadence" rather than "momentum": call cadence and visit cadence are
 * established sales vocabulary, so the panel names the thing reps are
 * already trying to build. Momentum was generic where this is specific.
 * Someone with no rep role sees "Your Activity" instead — they cannot
 * log a visit, so they have no visit cadence to speak of.
 *
 * GitHub's contribution graph, in miniature: weekday rows, one column
 * per week, month labels along the top. Small unlabelled squares with
 * the exact date in the tooltip — the layout carries the calendar
 * structure, so printing a number in every cell only adds bulk.
 *
 * The earlier version put a numbered month grid next to a progress
 * ring and took a quarter of the page for a secondary widget. This
 * shape is roughly a fifth the height and reads at a glance, which is
 * what a panel you pass on the way somewhere else needs to do.
 *
 * WHY THIS IS NOT A STREAK
 * ------------------------
 * A consecutive-day counter resets to zero, and a number that erases
 * six weeks of work because somebody caught the flu stops being
 * motivating the first time it happens — the reliable outcome is that
 * people stop looking at it.
 *
 * A rolling window can't do that. A quiet day nudges it down by one, a
 * good day pulls it back up, and there is no state in which the panel
 * tells a rep they lost everything. That property is the entire design
 * and everything below serves it: there is no "best ever" to fall
 * short of, no red, and no empty state phrased as a deficit.
 *
 * WHY THREE STATES
 * ----------------
 * Binary would turn every admin day, travel day and day off into an
 * identical blank, and a mostly-blank grid reads as failure however
 * the copy is worded. Separating "showed up" from "nothing at all" is
 * what keeps a normal working month looking normal.
 *
 * WHY A ROLLING WINDOW AND NOT A CALENDAR MONTH
 * ---------------------------------------------
 * "This month" would reset every rep to 0 on the 1st — the same
 * wipe-the-slate behaviour a streak has, just on a slower clock, and
 * it lands hardest on someone who finished the previous month strong.
 * It would also make the denominator wobble between 28 and 31, so
 * 18/28 and 18/31 would mean different things month to month.
 *
 * The rolling window has neither problem: always exactly N real
 * calendar days back from today (date arithmetic, so leap years and
 * short months take care of themselves). The copy says "the last 30
 * days" and never "this month", because those are not the same period.
 */

import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';

import {
  useMomentum,
  useTeamPulse,
  type AddedAccount,
  type Momentum,
  type MomentumDay,
  type TeamPulse,
  type VisitedAccount,
} from '../api/field';
import { isRepSurfacePath, useActAsRep } from '../api/actAs';
import { useAuth } from '../auth/useAuth';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = '#fdf9f0';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

/** Shared by the card and its loading skeleton, so the placeholder
 *  occupies exactly the space the card will — no jump on load. */
const PANEL_MAX_WIDTH = 940;
const PANEL_HEIGHT = 232;

/**
 * Cell geometry. Square, always.
 *
 * An earlier version stretched cells to fill the card width, which
 * turned them into wide bars and stopped the grid reading as a
 * calendar at all. GitHub fills its width by having 52 columns, not by
 * distorting the squares — so the cells stay square and the card is
 * sized to the grid instead of the other way round.
 */
const CELL = 19;
const CELL_GAP = 4;

/**
 * The intensity ramp, weakest to strongest:
 *
 *   nothing → opened the app → 1–2 visits → 3+ visits
 *
 * Deliberately all warm and never red, because no day on this grid is
 * a failure — a rep with an admin day should not see a mark that reads
 * as a fault.
 *
 * Busy days get their own darker step rather than flattening into one
 * "logged" tone: a day with five visits and a day with one are not the
 * same day's work, and the ramp is what makes a heavy week visible as
 * a dense patch rather than a uniform block.
 */
const TONE_QUIET = 'rgba(15, 10, 7, 0.055)';
/** Days still to come. Fainter than quiet and never counted — an
 *  outline of a square rather than a filled one, so the runway reads
 *  as space to fill rather than as days already missed. */
const TONE_FUTURE = 'transparent';
const TONE_SEEN = 'rgba(187, 140, 63, 0.26)';
const TONE_LOGGED_LIGHT = 'rgba(187, 140, 63, 0.60)';
const TONE_LOGGED_DEEP = '#9c7430';

/**
 * The ring that marks today.
 *
 * Defined once and used by both the grid cell and the header legend —
 * they drifted apart when each carried its own copy, leaving the
 * legend explaining an ink ring while the grid drew a gold one.
 *
 * Drawn as a box-shadow so it costs no layout space inside the grid,
 * which is also why anything placing it next to text has to leave
 * room for the ~3px it spills outward.
 */
const TODAY_RING = `0 0 0 2px ${IVORY}, 0 0 0 3.5px ${GOLD}`;
const TODAY_RING_SPILL = 3.5;

/** Where a day sits on the ramp above. */
function toneFor(day: MomentumDay): string {
  if (day.state === 'future') return TONE_FUTURE;
  if (day.state === 'quiet') return TONE_QUIET;
  if (day.state === 'seen') return TONE_SEEN;
  return day.visits >= 3 ? TONE_LOGGED_DEEP : TONE_LOGGED_LIGHT;
}

/** Full names, as GitHub has them — a bare "T" is ambiguous twice. */
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Only alternate rows get a label, or the gutter is a wall of text. */
const LABELLED_ROWS = new Set([0, 2, 4]);

// =====================================================================
// Date helpers
// =====================================================================
/** Days from Monday. JS getDay() is Sunday-first; the week here is not. */
function mondayOffset(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function parseDay(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

function isoOf(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

interface Cell {
  date: Date;
  /** Null for padding — a real calendar day, but outside the window. */
  day: MomentumDay | null;
}

/** Columns of 7, oldest week first, each column running Monday→Sunday. */
function buildColumns(days: MomentumDay[]): Cell[][] {
  const first = days.at(0);
  const last = days.at(-1);
  if (first === undefined || last === undefined) return [];

  const byIso = new Map(days.map((d) => [d.day, d]));
  const firstDate = parseDay(first.day);
  const lastDate = parseDay(last.day);

  // Pad out to whole weeks so every column is a full 7 and the
  // weekday rows line up across the grid.
  const cursor = new Date(firstDate);
  cursor.setDate(cursor.getDate() - mondayOffset(firstDate));
  const gridEnd = new Date(lastDate);
  gridEnd.setDate(gridEnd.getDate() + (6 - mondayOffset(lastDate)));

  const columns: Cell[][] = [];
  while (cursor <= gridEnd) {
    const week: Cell[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ date: new Date(cursor), day: byIso.get(isoOf(cursor)) ?? null });
      cursor.setDate(cursor.getDate() + 1);
    }
    columns.push(week);
  }
  return columns;
}

/**
 * Month label for a column, or '' for none.
 *
 * A week belongs to the month containing its THURSDAY — the rule
 * ISO-8601 uses for week numbering. Labelling by the column's first
 * day instead would put "Aug" over a column reading Jul 27–Aug 2,
 * five days of which are July.
 */
function columnMonthLabel(column: Cell[], previous: Cell[] | undefined): string {
  const thursday = column.at(3);
  if (thursday === undefined) return '';

  const label = thursday.date.toLocaleDateString(undefined, { month: 'short' });
  const previousThursday = previous?.at(3);
  if (previousThursday === undefined) return label;

  return previousThursday.date.getMonth() === thursday.date.getMonth() ? '' : label;
}

/**
 * Month labels for the whole grid, with collisions dropped.
 *
 * A label is wider than the column it sits over, so two months
 * starting a week apart render as "JUL AUG" run together. Whenever a
 * label lands within two columns of the previous one, the older of the
 * two is dropped — the newer month is the one someone is reading
 * towards, and a partial month at the very left edge is the least
 * useful label on the row.
 */
const MIN_LABEL_GAP = 2;

function monthLabels(columns: Cell[][]): string[] {
  const labels = columns.map((column, i) => columnMonthLabel(column, columns[i - 1]));

  let lastAt = -Infinity;
  labels.forEach((label, i) => {
    if (!label) return;
    if (i - lastAt < MIN_LABEL_GAP) labels[lastAt] = '';
    lastAt = i;
  });
  return labels;
}

/** "Tue, 25 Aug" — the heading of a day's tooltip. */
function dayHeading(d: MomentumDay): string {
  return parseDay(d.day).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * One line summarising a day, for the tooltip's headline.
 *
 * Deliberately not the whole story — the rows underneath carry that.
 * This is the sentence someone reads first, so it names the shape of
 * the day rather than enumerating it.
 */
function daySummary(d: MomentumDay): string {
  if (d.state === 'future') return 'Still to come';
  if (d.state === 'quiet') return 'Nothing logged';

  const bits: string[] = [];
  if (d.visits > 0) bits.push(`${d.visits} ${d.visits === 1 ? 'visit' : 'visits'}`);
  if (d.flags > 0) bits.push(`${d.flags} ${d.flags === 1 ? 'flag' : 'flags'}`);

  // `seen` with nothing else: they turned up and logged nothing. Worth
  // saying plainly rather than leaving the tooltip almost empty.
  if (bits.length === 0) return 'Opened the app';
  return bits.join(' · ');
}

/**
 * Everything that happened on a day, as tooltip rows.
 *
 * A cell can only be one colour, so the shade alone can't say that
 * somebody opened the app AND logged two calls AND flagged an account.
 * These rows can.
 */
function dayEntries(d: MomentumDay): Array<{ title: string; meta?: string; count?: number }> {
  if (d.state === 'future' || d.state === 'quiet') return [];

  const entries: Array<{ title: string; meta?: string; count?: number }> = [];

  for (const [key, count] of Object.entries(d.channels).sort((a, b) => b[1] - a[1])) {
    entries.push({ title: CHANNEL_LABELS[key] ?? key, count });
  }
  if (d.flags > 0) entries.push({ title: 'Flagged for review', count: d.flags });
  if (d.opened) entries.push({ title: 'Opened the app' });

  return entries;
}

/** "Jaguar Stadium, Whiskey Willy's +2 more" — where the day was spent. */
function dayAccountsNote(d: MomentumDay): string | undefined {
  if (d.accounts.length === 0) return undefined;
  const extra = d.account_total - d.accounts.length;
  const names = d.accounts.join(', ');
  return extra > 0 ? `${names} +${extra} more` : names;
}

/**
 * "Aug 20 – 26", or "August 2026" when the range is a whole month.
 *
 * Stated outright because "the last 28 days" is still an abstraction,
 * and the small squares no longer carry their own dates. Naming the
 * two ends is what makes the grid checkable.
 *
 * The month is not repeated on both sides of a same-month range — the
 * second "Aug" in "Aug 20 – Aug 26" is noise, and at a glance the
 * repetition reads as two separate months.
 */
function windowRange(m: Momentum): string {
  const first = m.days.at(0);
  // The measured window ends at today; the runway past it is not part
  // of what the headline counts, so the range must not claim it.
  const last = m.days.at(m.window_days - 1);
  if (first === undefined || last === undefined) return '';

  const from = new Date(`${first.day}T00:00:00`);
  const to = new Date(`${last.day}T00:00:00`);

  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
  const crossesYear = from.getFullYear() !== to.getFullYear();

  // A window that happens to cover one calendar month exactly reads
  // better by name than as two dates restating the month twice.
  const isWholeMonth =
    sameMonth &&
    from.getDate() === 1 &&
    to.getDate() === new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
  if (isWholeMonth) {
    return from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  const fmt = (d: Date, withMonth: boolean) =>
    d.toLocaleDateString(undefined, {
      ...(withMonth ? { month: 'short' } : {}),
      day: 'numeric',
      ...(crossesYear ? { year: 'numeric' } : {}),
    });

  return `${fmt(from, true)} – ${fmt(to, !sameMonth)}`;
}

/**
 * The encouraging line.
 *
 * Every branch is forward-looking. The weakest case anyone can land in
 * still reads as an invitation rather than a scolding — someone
 * opening this after two hard weeks should not be met with a number
 * and an implied complaint.
 */
function encouragement(m: Momentum, logsVisits: boolean, subject: 'you' | 'rep'): string {
  const { active_days: active, seen_days: seen, window_days: window } = m;
  const you = subject === 'you';

  // Someone with no rep role can't log a visit at all, so measuring
  // them on logged work would show a permanent zero and read as a
  // reproach for not doing a job they don't have. Their number counts
  // turning up, which is the thing they actually control.
  if (!logsVisits) {
    const present = active + seen;
    if (present === 0) return 'Your days here will show up as you use the platform.';
    if (present >= window * 0.6) return 'You’re in here most days.';
    return 'Building a picture of your time on the platform.';
  }

  // Second person addresses the rep; third person is for an admin
  // reading someone else's panel. Encouragement aimed at the wrong
  // person reads as the app not knowing who is holding it.
  if (active === 0) {
    // Name the days they HAVE been here. The grid already shows them as
    // the lighter shade, but a headline of 0 next to wordless squares
    // reads as "you did nothing" to someone who knows they showed up.
    // Saying the number out loud credits the turning-up without letting
    // it count as field work.
    const here = `${seen} ${seen === 1 ? 'day' : 'days'} here`;
    if (you) {
      return seen > 0
        ? `${here} — log a visit and this starts filling up.`
        : 'Log a visit and this starts filling up.';
    }
    return seen > 0 ? `${here}, nothing logged yet.` : 'Nothing logged in this window.';
  }
  if (active >= window * 0.6) {
    return you ? 'Strong stretch — you’re out there most days.' : 'Strong stretch — out most days.';
  }
  if (active >= window * 0.3) {
    return you ? 'Steady stretch. Every logged visit counts.' : 'Steady stretch.';
  }
  return you ? 'Off to a start. One more day out there moves this.' : 'Off to a start.';
}

// =====================================================================
// Pieces
// =====================================================================
const MotionBox = motion.create(Box);

/**
 * Today's date, as the grid sees it.
 *
 * The measured window always ends at today and the runway follows, so
 * today is the last cell that isn't `future`. Falls back to the final
 * cell for a series with no runway at all.
 */
function lastMeasuredDay(days: MomentumDay[]): string {
  for (let i = days.length - 1; i >= 0; i -= 1) {
    const day = days[i];
    if (day && day.state !== 'future') return day.day;
  }
  return days.at(-1)?.day ?? '';
}

/**
 * Cells arrive in a diagonal sweep rather than all at once.
 *
 * The delay is derived from the cell's position, so the grid resolves
 * from oldest to newest and lands on today — the eye follows the fill
 * to the day that matters instead of being handed a finished block.
 * ~9ms per column keeps the whole sweep under half a second.
 */
const SWEEP_PER_COLUMN = 0.009;
const SWEEP_PER_ROW = 0.012;

function DayCell({
  cell,
  isToday,
  column,
  row,
  animate,
}: {
  cell: Cell;
  isToday: boolean;
  column: number;
  row: number;
  animate: boolean;
}) {
  const { day } = cell;
  const isFuture = day?.state === 'future';

  const box = (
    <MotionBox
      initial={animate ? { opacity: 0, scale: 0.4 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.32,
        ease: [0.22, 1, 0.36, 1],
        delay: animate ? column * SWEEP_PER_COLUMN + row * SWEEP_PER_ROW : 0,
      }}
      whileHover={day === null ? undefined : { scale: 1.28, zIndex: 1 }}
      sx={{
        width: CELL,
        height: CELL,
        borderRadius: '4px',
        bgcolor: day === null ? 'transparent' : toneFor(day),
        // The runway is drawn, not filled — a hairline reads as "not
        // yet" where a grey block would read as "missed". Solid and
        // faint rather than dashed: eight columns of dashes turned
        // into visual static.
        border: isFuture ? '1px solid rgba(187, 140, 63, 0.28)' : '1px solid transparent',
        // Weekends recede as a band. Reps don't work them, so two of
        // seven rows are near-permanently empty; at full strength that
        // dead band reads as a fortnight of failure at the bottom of
        // every grid. Muted, it reads as the weekend. A weekend that
        // does carry work still shows — the opacity is on the cell,
        // not on whether it's drawn.
        // Only genuinely empty weekends recede. A weekend that carries
        // work — logged OR merely seen — stays at full strength: `seen`
        // days count toward a non-rep's headline, and muting them made
        // the number disagree with the grid.
        opacity: day?.is_weekend && (day.state === 'quiet' || day.state === 'future') ? 0.4 : 1,
        boxSizing: 'border-box',
        cursor: day === null ? 'default' : 'pointer',
        // Today wears a soft gold ring. The previous hard black outline
        // read as a focus artefact — a browser had selected something —
        // rather than as "you are here".
        boxShadow: isToday ? TODAY_RING : 'none',
      }}
    />
  );

  // Padding days get no tooltip — there is nothing true to say about a
  // day the window doesn't cover.
  if (day === null) return box;

  return (
    <Tooltip
      title={
        <MetricCard
          label={dayHeading(day)}
          headline={daySummary(day)}
          entries={dayEntries(day)}
          note={dayAccountsNote(day)}
        />
      }
      arrow
      enterTouchDelay={0}
      placement="top"
      slotProps={CARD_TOOLTIP_PROPS}
    >
      {box}
    </Tooltip>
  );
}

function ContributionGrid({ days, compact = false }: { days: MomentumDay[]; compact?: boolean }) {
  const columns = buildColumns(days);
  // The LAST measured day, not the first. `days` runs oldest-first
  // with the runway at the end, so `find` returned the start of the
  // window and put the ring on the oldest cell instead of today.
  const todayIso = lastMeasuredDay(days);
  const headerRow = compact ? '0px' : '14px';
  const reduceMotion = useReducedMotion();
  const animate = !compact && !reduceMotion;
  const labels = monthLabels(columns);

  return (
    <Box sx={{ display: 'flex', gap: `${CELL_GAP}px` }}>
      {!compact && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: `${headerRow} repeat(7, ${CELL}px)`,
            gap: `${CELL_GAP}px`,
            mr: 0.5,
          }}
        >
          <Box />
          {WEEKDAY_NAMES.map((letter, i) => (
            <Typography
              key={`${letter}-${i}`}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: `${CELL}px`,
                letterSpacing: '0.02em',
                color: 'rgba(15, 10, 7, 0.32)',
                whiteSpace: 'nowrap',
                opacity: i >= 5 ? 0.5 : 1,
              }}
            >
              {LABELLED_ROWS.has(i) ? letter : ''}
            </Typography>
          ))}
        </Box>
      )}

      {columns.map((column, ci) => (
        <Box
          key={isoOf(column[0]?.date ?? new Date())}
          sx={{
            display: 'grid',
            gridTemplateRows: `${headerRow} repeat(7, ${CELL}px)`,
            gap: `${CELL_GAP}px`,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgba(187, 140, 63, 0.85)',
              whiteSpace: 'nowrap',
              lineHeight: headerRow,
            }}
          >
            {compact ? '' : labels[ci]}
          </Typography>

          {column.map((cell, ri) => (
            <DayCell
              key={isoOf(cell.date)}
              cell={cell}
              isToday={cell.day !== null && cell.day.day === todayIso}
              column={ci}
              row={ri}
              animate={animate}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function Legend({ logsVisits = true }: { logsVisits?: boolean }) {
  const swatches = logsVisits
    ? [TONE_QUIET, TONE_SEEN, TONE_LOGGED_LIGHT, TONE_LOGGED_DEEP]
    : [TONE_QUIET, TONE_SEEN];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15,10,7,0.5)' }}>
        {logsVisits ? 'Nothing' : 'Away'}
      </Typography>
      {swatches.map((tone) => (
        <Box key={tone} sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: tone }} />
      ))}
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15,10,7,0.5)' }}>
        {logsVisits ? 'More visits' : 'Here'}
      </Typography>

      {/* The runway needs naming — a dashed square is otherwise a
          puzzle, and the whole point is that it reads as "yours to
          fill" rather than as a day already lost. */}
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: '2px',
          border: '1px solid rgba(187, 140, 63, 0.35)',
          boxSizing: 'border-box',
          ml: 1,
        }}
      />
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15,10,7,0.5)' }}>
        Ahead
      </Typography>
    </Box>
  );
}

/**
 * Shared surface for the metric tooltips.
 *
 * Ivory on a gold hairline, matching the panel it explains. The first
 * version inherited MUI's near-black default, which put a dark slab on
 * a warm cream page — the tooltip looked like it belonged to a
 * different product. A tooltip is part of the surface it annotates.
 */
const CARD_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: '#fffdf8',
      color: INK,
      borderRadius: 2.5,
      px: 1.75,
      py: 1.35,
      maxWidth: 320,
      border: '1px solid rgba(187, 140, 63, 0.34)',
      boxShadow: '0 2px 6px rgba(15, 10, 7, 0.05), 0 18px 42px -16px rgba(139, 101, 38, 0.42)',
    },
  },
  arrow: {
    sx: {
      color: '#fffdf8',
      // MUI draws the arrow as a filled triangle with no border, so
      // without this it reads as a pale notch punched out of the card's
      // gold outline.
      '&::before': { border: '1px solid rgba(187, 140, 63, 0.34)' },
    },
  },
} as const;

const RULE = '1px solid rgba(187, 140, 63, 0.22)';

/**
 * A structured tooltip card.
 *
 *   VISITS
 *   16 logged in the last 28 days
 *   ─────────────────────────────
 *   In-person visit  ▓▓▓▓▓▓▓▓  12
 *   Phone call       ▓▓          4
 *   ─────────────────────────────
 *   Every channel counts the same.
 *
 * A paragraph in a tooltip is barely better than no tooltip — people
 * skim it and leave still guessing. Splitting it into a label, a
 * figure, an itemised breakdown, and one line of definition means each
 * part can be read on its own, and the breakdown answers the question
 * the bare number provokes: sixteen of *what*.
 *
 * Rows carry a proportion bar because the ranking is the point. "12
 * and 4" is a fact; a bar three times longer than its neighbour is the
 * shape of the month, read without doing arithmetic.
 */
function MetricCard({
  label,
  headline,
  rows,
  entries,
  total,
  note,
}: {
  label: string;
  headline: string;
  /** Itemised breakdown, each with a figure. */
  rows?: Array<[string, number]>;
  /** Named entries — a title, an optional second line of context, and
   *  an optional figure on the right. Putting these through `rows`
   *  would flatten "added by Chris on Tuesday" into a bare number. */
  entries?: Array<{ title: string; meta?: string; count?: number }>;
  /** How many exist in total, when `entries` is only a sample. Renders
   *  a "+N more" tail so a truncated list never passes as complete. */
  total?: number;
  note?: string;
}) {
  const hasRows = (rows?.length ?? 0) > 0 || (entries?.length ?? 0) > 0;
  const peak = Math.max(1, ...(rows ?? []).map(([, count]) => count));

  return (
    <Box sx={{ minWidth: 208 }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 0.4,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 14,
          fontWeight: 700,
          color: INK,
          lineHeight: 1.35,
          mb: hasRows || note ? 0.9 : 0,
        }}
      >
        {headline}
      </Typography>

      {hasRows && (
        <Box sx={{ borderTop: RULE, pt: 0.85, mb: note ? 0.85 : 0 }}>
          {entries?.map((entry, index) => (
            <Box
              // Index-keyed: two reps can share a name, and two
              // accounts can share a name and an added-on date.
              key={`${index}-${entry.title}`}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 0.55,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: INK,
                    lineHeight: 1.3,
                  }}
                >
                  {entry.title}
                </Typography>
                {entry.meta && (
                  <Typography
                    sx={{
                      fontFamily: FONT_INTER,
                      fontSize: 10.5,
                      color: 'rgba(15, 10, 7, 0.5)',
                      lineHeight: 1.3,
                    }}
                  >
                    {entry.meta}
                  </Typography>
                )}
              </Box>

              {entry.count !== undefined && entry.count > 1 && (
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 11,
                    fontWeight: 700,
                    color: GOLD,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ×{entry.count}
                </Typography>
              )}
            </Box>
          ))}

          {entries !== undefined && total !== undefined && total > entries.length && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                fontStyle: 'italic',
                color: 'rgba(15, 10, 7, 0.45)',
                mt: 0.2,
              }}
            >
              +{total - entries.length} more
            </Typography>
          )}

          {rows?.map(([name, count]) => (
            <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 12.5,
                  color: 'rgba(15, 10, 7, 0.72)',
                  flexShrink: 0,
                  minWidth: 94,
                }}
              >
                {name}
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(187, 140, 63, 0.13)',
                  overflow: 'hidden',
                  minWidth: 34,
                }}
              >
                <Box
                  sx={{
                    width: `${Math.round((count / peak) * 100)}%`,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: GOLD,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: INK,
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 18,
                  textAlign: 'right',
                }}
              >
                {count}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {note && (
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            lineHeight: 1.5,
            color: 'rgba(15, 10, 7, 0.6)',
            borderTop: hasRows ? 'none' : RULE,
            pt: hasRows ? 0 : 0.85,
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );
}

/** Channel keys as the API sends them → what a person calls them. */
const CHANNEL_LABELS: Record<string, string> = {
  visit: 'In-person visit',
  call: 'Phone call',
  email: 'Email',
  event: 'Event',
};

/** Busiest channel first — the shape of the week, not the API's order. */
function channelRows(byChannel: Record<string, number>): Array<[string, number]> {
  return Object.entries(byChannel)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => [CHANNEL_LABELS[key] ?? key, count]);
}

/** "24 Aug" — short enough to sit beside a name without wrapping. */
function addedOn(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * The second line under a reached account.
 *
 * Place first, then when, then who — but only the parts that add
 * something. On your own list "you" is noise, so the visitor is named
 * only on the team's. An account with no city on file simply drops
 * that part rather than showing an empty separator.
 */
function reachedMeta(a: VisitedAccount, scope: 'you' | 'team' | 'rep'): string {
  const place = [a.city, a.state_code].filter(Boolean).join(', ');
  const parts = [place, addedOn(a.last_visit_on)];
  if (scope === 'team') parts.push(a.last_visit_by);
  return parts.filter(Boolean).join(' · ');
}

/**
 * The three activity figures, phrased as a relationship.
 *
 * They were three peer numbers in a row — "16 visits  16 accounts
 * 6 new" — which read as a duplicate or a bug whenever two of them
 * matched. And they match often: in early use most accounts get
 * exactly one visit, so visits and accounts are the same number for
 * an honest reason nobody can see from the layout.
 *
 * Written as "16 visits across 16 accounts" the equality explains
 * itself — one visit each — and the two figures stop competing to be
 * read as separate facts.
 *
 * The three are also NOT disjoint, which is worth saying somewhere: a
 * hand-added account that has since been visited is counted in both
 * `accounts` and `new`. The tooltips say so rather than leaving
 * someone to discover it by finding the totals don't add up.
 */
function StatLine({
  visits,
  accounts,
  accountNames,
  newAccounts,
  newAccountNames,
  windowDays,
  channels,
  scope,
}: {
  visits: number;
  accounts: number;
  accountNames: VisitedAccount[];
  newAccounts: number;
  newAccountNames: AddedAccount[];
  windowDays: number;
  channels: Record<string, number>;
  scope: 'you' | 'team' | 'rep';
}) {
  const mine = scope !== 'team';
  const who = scope === 'you' ? 'you' : scope === 'rep' ? 'this rep' : 'the team';

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, flexWrap: 'wrap' }}>
      <Counter
        value={visits}
        label="visits"
        hint={
          <MetricCard
            label={mine ? 'Visits' : 'Team visits'}
            headline={`${visits} logged in the last ${windowDays} days`}
            rows={channelRows(channels)}
            note={`Every channel counts the same — a call ${who} logged is a visit here.`}
          />
        }
      />

      <Typography sx={{ fontFamily: FONT_INTER, fontSize: 12, color: 'rgba(15, 10, 7, 0.45)' }}>
        across
      </Typography>

      <Counter
        value={accounts}
        label={accounts === 1 ? 'account' : 'accounts'}
        hint={
          <MetricCard
            label={mine ? 'Accounts reached' : 'Team accounts reached'}
            headline={`${accounts} distinct ${accounts === 1 ? 'place' : 'places'}`}
            entries={accountNames.map((a) => ({
              title: a.name,
              meta: reachedMeta(a, scope),
              count: a.visits,
            }))}
            total={accounts}
            note={
              accounts === 0
                ? 'Nothing logged in this window yet.'
                : visits === accounts
                  ? 'Busiest first, most recent visit shown. One visit each — no repeat calls in this window yet.'
                  : `Busiest first, most recent visit shown. Repeat visits count once, so ${visits} logs land on ${accounts} accounts.`
            }
          />
        }
      />

      {newAccounts > 0 && (
        <>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: 12, color: 'rgba(15, 10, 7, 0.45)' }}>
            ·
          </Typography>
          <Counter
            value={newAccounts}
            label="added"
            hint={
              <MetricCard
                label="Added by hand"
                headline={`${newAccounts} new ${newAccounts === 1 ? 'account' : 'accounts'}`}
                entries={newAccountNames.map((a) => ({
                  title: a.name,
                  meta: `${a.added_by} · ${addedOn(a.added_on)}`,
                }))}
                total={newAccounts}
                note={`Newest first. Places ${who} entered because the depletions feed didn't have them yet. Any since visited also count in the accounts figure, so these three don't sum.`}
              />
            }
          />
        </>
      )}
    </Box>
  );
}

/**
 * Numbers animate to their value rather than snapping.
 *
 * A statistic that ticks into place reads as something that just
 * happened; the same number rendered flat reads as a value that has
 * always been there.
 *
 * Counts from the previous value, not from zero. On first paint that
 * is zero anyway, but when the range toggle changes the target,
 * restarting at zero made all six figures on the card drop out and
 * climb back — which read as the whole panel reloading rather than as
 * a filter being applied. Skipped entirely under reduced motion.
 */
function useCountUp(target: number, enabled: boolean): number {
  const [value, setValue] = useState(enabled ? 0 : target);
  const frame = useRef<number>(0);
  const shown = useRef(enabled ? 0 : target);

  // Kept in a ref so the effect can read where the number currently
  // sits without listing it as a dependency and restarting itself.
  shown.current = value;

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }

    const from = shown.current;
    if (from === target) return;

    const DURATION = 700;
    const started = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / DURATION);
      // Ease-out cubic: fast first, settling rather than stopping.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
    // `shown` is a ref on purpose: including the live value here would
    // restart the animation on every frame it produces.
  }, [target, enabled]);

  return value;
}

function Counter({
  value,
  label,
  large = false,
  hint,
}: {
  value: number;
  label: string;
  large?: boolean;
  hint?: ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  const shown = useCountUp(value, !reduceMotion);

  const body = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.5,
        cursor: hint ? 'help' : 'default',
        // A dotted underline is the long-standing convention for
        // "there is an explanation here". Without it the tooltip is
        // undiscoverable and may as well not exist.
        borderBottom: hint ? '1px dotted rgba(15, 10, 7, 0.20)' : 'none',
        transition: 'border-color 160ms ease',
        '&:hover': hint ? { borderBottomColor: GOLD } : {},
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: large ? 25 : 17,
          color: INK,
          lineHeight: 1,
          letterSpacing: '-0.01em',
          // Tabular figures so a counting number doesn't jitter its
          // neighbours sideways as digits change width.
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {shown}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: large ? 12.5 : 12,
          color: 'rgba(15, 10, 7, 0.5)',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (!hint) return body;

  return (
    <Tooltip title={hint} arrow placement="top" enterTouchDelay={0} slotProps={CARD_TOOLTIP_PROPS}>
      {body}
    </Tooltip>
  );
}

export function MomentumPanel() {
  const [range, setRange] = useState(RANGES[1]?.days ?? 28);
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();

  // Whose panel this is, not who is looking at it.
  //
  // The API is impersonation-aware, so an admin on "Act as rep" is
  // served that rep's numbers. Branching the presentation on the
  // VIEWER's roles put the two halves out of step: a rep's data
  // rendered with the copy written for someone who can't log a visit.
  //
  // The path test is not optional. `actAs` lives in sessionStorage and
  // outlives the page that set it, but the API client only attaches
  // the impersonation header on rep-surface paths — so on the home
  // page the numbers are the VIEWER's while the stored value still
  // names somebody else. Reading it unconditionally put a colleague's
  // name over your own figures. Same predicate as the client and the
  // "Acting as" banner, so the three can never disagree about whether
  // impersonation is in force.
  const { pathname } = useLocation();
  const storedActAs = useActAsRep();
  const actingAs = isRepSurfacePath(pathname) ? storedActAs : null;
  const actingAsRep = actingAs !== null;

  // Also keys the cache, so an impersonated view and the personal home
  // page can never share an entry.
  const query = useMomentum(range, actingAs?.id ?? null);
  const logsVisits = actingAsRep || !!user?.roles.some((r) => r.name === 'field_rep');

  if (query.isPending) {
    // Same geometry as the loaded card. It was 620px wide and not
    // centred, so on every reload the placeholder sat left of where
    // the card lands and then jumped across.
    return (
      <Skeleton
        variant="rounded"
        height={PANEL_HEIGHT}
        sx={{ borderRadius: 3, width: '100%', maxWidth: PANEL_MAX_WIDTH, mx: 'auto' }}
      />
    );
  }

  // Silent on failure. This panel is encouragement, not information —
  // an error banner where the nice thing should be is worse than the
  // panel simply not being there, and nothing else depends on it.
  if (query.isError || !query.data) return null;

  const m = query.data;
  const headline = logsVisits ? m.active_days : m.active_days + m.seen_days;

  return (
    <MotionBox
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        px: { xs: 2, md: 2.5 },
        pt: { xs: 1.75, md: 2 },
        pb: { xs: 1.75, md: 2 },
        borderRadius: 3,
        // A soft vertical wash rather than flat ivory — the flat fill
        // read as a grey box sitting on the page.
        background: `linear-gradient(160deg, #fffdf8 0%, ${IVORY} 62%, #faf3e6 100%)`,
        border: '1px solid rgba(187, 140, 63, 0.20)',
        boxShadow: '0 1px 2px rgba(15, 10, 7, 0.03), 0 8px 28px -14px rgba(139, 101, 38, 0.28)',
        // Bounded rather than full-bleed. There is roughly 800px of
        // content here; stretched across the full tile grid it left a
        // third of the card empty on the right. Capping the measure
        // and centring it means the card is deliberately narrower than
        // the row above rather than accidentally under-filled.
        width: '100%',
        maxWidth: PANEL_MAX_WIDTH,
        mx: 'auto',
      }}
    >
      {/* One control for the whole card. Two toggles — one per column
          — invited the question of whether they were independent, and
          made the same decision twice. The dates it resolves to sit
          beside it, so "Month" is never just a word on a button. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid transparent',
          borderImage:
            'linear-gradient(90deg, rgba(187,140,63,0.26), rgba(187,140,63,0.10), transparent) 1',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            fontWeight: 600,
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          {windowRange(m)}
        </Typography>

        {/* Says which square the ring marks. Without it the outlined
            cell is just an empty square with a border, and people
            read it as a rendering glitch rather than as "you". */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // The ring spills outside the swatch's box without taking
            // layout space, so the gap has to clear it or it collides
            // with the word beside it.
            gap: `${TODAY_RING_SPILL + 5}px`,
            ml: `${TODAY_RING_SPILL}px`,
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '3px',
              bgcolor: TONE_SEEN,
              boxShadow: TODAY_RING,
            }}
          />
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15, 10, 7, 0.45)' }}
          >
            today
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <RangeToggle value={range} onChange={setRange} />
        </Box>
      </Box>

      {/* Three zones across: the grid, what it says about you, and
          what the team did. Previously the team sat in a strip below,
          which made a tall narrow card floating under a full-width row
          of section tiles. Across, it matches the page's rhythm and
          leaves no hole in the middle column. */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 2.5, md: 3 },
          flexWrap: 'wrap',
          // All three columns start at the same y. Centring them
          // against the grid — the tallest element — left the two
          // headings floating below its top edge, so nothing on the
          // card shared a line with anything else.
          alignItems: 'flex-start',
        }}
      >
        {/* Minimum width pinned to the wider range. Without it the
            card lurches narrower the moment someone picks Week, which
            reads as the layout breaking rather than as a filter. */}
        <Box sx={{ overflowX: 'auto', flexShrink: 0, minWidth: { xs: 0, sm: 236 } }}>
          <ContributionGrid days={m.days} />
        </Box>

        <Box sx={{ minWidth: 190, flex: '1 1 0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: GOLD,
                whiteSpace: 'nowrap',
              }}
            >
              {actingAsRep
                ? `${actingAs?.name ?? 'This rep'}’s Cadence`
                : logsVisits
                  ? 'Your Cadence'
                  : 'Your Activity'}
            </Typography>

            {/* The run only appears once it means something. "1 day
                going" on someone's first afternoon is noise, and
                "0 days" would be a rebuke. */}
            {logsVisits && m.current_run >= 2 && (
              <MotionBox
                initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45, type: 'spring', stiffness: 420, damping: 22 }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  px: 0.75,
                  py: 0.125,
                  borderRadius: 10,
                  bgcolor: 'rgba(187, 140, 63, 0.16)',
                }}
              >
                <LocalFireDepartmentIcon sx={{ fontSize: 11, color: GOLD }} />
                <Typography
                  sx={{ fontFamily: FONT_INTER, fontSize: 10, fontWeight: 700, color: GOLD }}
                >
                  {m.current_run} days
                </Typography>
              </MotionBox>
            )}
          </Box>

          <Counter
            value={headline}
            label={`of ${m.window_days} days`}
            large
            hint={
              logsVisits ? (
                <MetricCard
                  label={actingAsRep ? 'Cadence' : 'Your Cadence'}
                  headline={`${headline} active of the last ${m.window_days} days`}
                  note={`Days ${
                    actingAsRep ? 'this rep' : 'you'
                  } logged a visit, call, email, event or account flag. Opening the app doesn't count towards this — ${
                    m.seen_days === 1 ? 'that was 1 day' : `there were ${m.seen_days} such days`
                  } in this window, shown as the lighter shade.`}
                />
              ) : (
                <MetricCard
                  label="Your Activity"
                  headline={`${headline} of the last ${m.window_days} days`}
                  note="Days you were on the platform. Logging visits is a Regional Sales Representative task, so this counts your time here instead."
                />
              )
            }
          />

          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15, 10, 7, 0.6)',
              lineHeight: 1.5,
              mt: 0.75,
              mb: 1.1,
            }}
          >
            {encouragement(m, logsVisits, actingAsRep ? 'rep' : 'you')}
          </Typography>

          {logsVisits && (
            <Box sx={{ mb: 1 }}>
              <StatLine
                visits={m.visits}
                accounts={m.accounts}
                accountNames={m.account_names}
                newAccounts={m.new_accounts}
                newAccountNames={m.new_account_names}
                windowDays={m.window_days}
                channels={m.visits_by_channel}
                scope={actingAsRep ? 'rep' : 'you'}
              />
            </Box>
          )}

          <Legend logsVisits={logsVisits} />
        </Box>

        <TeamStrip range={range} actAsId={actingAs?.id ?? null} />
      </Box>
    </MotionBox>
  );
}

/**
 * Ranges either panel can be read over.
 *
 * The numbers were previously stated with no period attached at all —
 * "16 visits" over an unstated window is not a fact, it is a puzzle.
 * Two ranges rather than a full date picker: week and month are the
 * two questions anyone actually asks, and a picker on a home-page
 * panel is a control nobody came here to operate.
 *
 * One control governs both columns. Separate toggles let you compare
 * your week against the team's month, but the far commoner reading is
 * "how did I do against everyone over the same stretch" — and two
 * controls made that require setting the same value twice.
 */
const RANGES: Array<{ days: number; label: string }> = [
  { days: 7, label: 'Week' },
  { days: 28, label: 'Month' },
];

function RangeToggle({ value, onChange }: { value: number; onChange: (days: number) => void }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        p: '2px',
        borderRadius: 10,
        bgcolor: 'rgba(187, 140, 63, 0.10)',
      }}
    >
      {RANGES.map((range) => {
        const active = range.days === value;
        return (
          <Box
            key={range.days}
            component="button"
            type="button"
            onClick={() => onChange(range.days)}
            sx={{
              border: 'none',
              cursor: 'pointer',
              px: 1.1,
              py: 0.3,
              borderRadius: 10,
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: active ? INK : 'rgba(15, 10, 7, 0.5)',
              bgcolor: active ? '#fffdf8' : 'transparent',
              boxShadow: active ? '0 1px 3px rgba(15, 10, 7, 0.10)' : 'none',
              transition: 'background-color 180ms ease, color 180ms ease',
              '&:hover': { color: INK },
            }}
          >
            {range.label}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The team's totals, under your own grid.
 *
 * Answers "am I the only one out here" without anyone having to ask,
 * which is the encouraging half of a shared surface. Totals only and
 * never a ranking — seeing the team logged 340 visits pulls people in;
 * seeing you placed 5th of 6 pushes five of them away.
 *
 * Hidden entirely when the team has done nothing in the window. A row
 * of zeroes is not encouragement, and on a quiet fortnight the honest
 * move is to say nothing rather than to announce the quiet.
 */
function TeamStrip({ range, actAsId }: { range: number; actAsId: string | null }) {
  const query = useTeamPulse(range, actAsId);
  if (query.isPending || query.isError || !query.data) return null;

  const t: TeamPulse = query.data;
  const days = t.window_days;
  // Quiet windows still render. Hiding the column would take the range
  // toggle with it the moment somebody switched to a slow week, which
  // reads as the control being broken.
  const quiet = t.visits === 0 && t.new_accounts === 0;

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.55, duration: 0.45 }}
      sx={{
        minWidth: 160,
        flex: '1 1 0',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        // A vertical hairline that fades at both ends, so the third
        // zone reads as a companion column rather than a bolted-on
        // panel. On a phone the columns stack, so it moves to the top
        // edge and becomes a horizontal rule instead.
        pl: { xs: 0, sm: 2.5 },
        pt: { xs: 1.5, sm: 0 },
        borderLeft: { xs: 'none', sm: '1px solid transparent' },
        borderTop: { xs: '1px solid transparent', sm: 'none' },
        borderImage: {
          xs: 'linear-gradient(90deg, transparent, rgba(187,140,63,0.32), transparent) 1',
          sm: 'linear-gradient(180deg, transparent, rgba(187,140,63,0.30), transparent) 1',
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 0.6,
        }}
      >
        The Team
      </Typography>

      {/* Names the reps who were out there. Only the active ones — a
          list of who has been quiet, on a page every rep can see, is a
          different thing from encouragement. */}
      <Tooltip
        arrow
        placement="top"
        enterTouchDelay={0}
        slotProps={CARD_TOOLTIP_PROPS}
        title={
          <MetricCard
            label="Reps active"
            headline={`${t.active_reps} of ${t.total_reps} reps, last ${days} days`}
            entries={t.active_rep_names.map((name) => ({ title: name }))}
            total={t.active_reps}
            note={
              t.active_rep_names.length > 0
                ? 'Reps who logged work in the window.'
                : 'Nobody has logged work in this window yet.'
            }
          />
        }
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.6)',
            cursor: 'help',
            borderBottom: '1px dotted rgba(15, 10, 7, 0.20)',
            transition: 'border-color 160ms ease',
            '&:hover': { borderBottomColor: GOLD },
          }}
        >
          {t.active_reps} of {t.total_reps} reps active
        </Typography>
      </Tooltip>

      <Box sx={{ mt: 0.9 }}>
        {quiet ? (
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: 13, color: 'rgba(15, 10, 7, 0.45)' }}>
            Nothing logged in this window yet.
          </Typography>
        ) : (
          <StatLine
            visits={t.visits}
            accounts={t.accounts}
            accountNames={t.account_names}
            newAccounts={t.new_accounts}
            newAccountNames={t.new_account_names}
            windowDays={days}
            channels={t.visits_by_channel}
            scope="team"
          />
        )}
      </Box>
    </MotionBox>
  );
}
